<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { openMarkdownLink } from '@/telegramWebApp'
import { renderMarkdown } from '@/todos/markdown'
import { formatTgTime } from '@/todos/tgTime'

const props = defineProps<{ source: string }>()
const emit = defineEmits<{ edit: [] }>()

const { locale } = useI18n()
const root = ref<HTMLElement | null>(null)
const html = computed(() => renderMarkdown(props.source))

// The fallback text of a date is the UTC time the bot wrote; the viewer sees it in their own timezone.
function localizeTimes(): void {
  for (const element of root.value?.querySelectorAll<HTMLElement>('time[data-unix]') ?? []) {
    const unix = Number(element.dataset.unix)
    const text = formatTgTime(unix, element.dataset.format ?? '', locale.value)
    if (text === null) continue
    element.textContent = text
    element.setAttribute('datetime', new Date(unix * 1000).toISOString())
  }
}

onMounted(localizeTimes)
watch([html, locale], () => void nextTick(localizeTimes))

function onClick(event: MouseEvent): void {
  const target = event.target as HTMLElement
  const spoiler = target.closest<HTMLElement>('[data-spoiler]')
  if (spoiler && !spoiler.classList.contains('revealed')) {
    spoiler.classList.add('revealed')
    return
  }
  const link = target.closest<HTMLElement>('a[data-href]')
  if (link) {
    event.preventDefault()
    openMarkdownLink(link.dataset.href ?? '')
    return
  }
  emit('edit')
}
</script>

<template>
  <!-- eslint-disable-next-line vue/no-v-html -- html comes from renderMarkdown, which escapes raw HTML -->
  <div ref="root" class="markdown" @click="onClick" v-html="html" />
</template>

<style scoped>
.markdown {
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--tg-secondary-bg-color);
  border-radius: 10px;
  overflow-wrap: anywhere;
}

.markdown :deep(> :first-child) {
  margin-top: 0;
}

.markdown :deep(> :last-child) {
  margin-bottom: 0;
}

.markdown :deep(a) {
  color: var(--tg-link-color);
}

.markdown :deep(blockquote) {
  margin: 0.4rem 0;
  padding: 0.1rem 0.7rem;
  border-left: 3px solid var(--tg-button-color);
  color: var(--tg-hint-color);
}

.markdown :deep(pre) {
  overflow-x: auto;
  padding: 0.4rem 0.6rem;
  border-radius: 8px;
  background: var(--tg-secondary-bg-color);
}

.markdown :deep(code) {
  font-family: ui-monospace, monospace;
  font-size: 0.9em;
}

.markdown :deep(.md-spoiler:not(.revealed)) {
  border-radius: 4px;
  background: var(--tg-hint-color);
  color: transparent;
  cursor: pointer;
  user-select: none;
}

.markdown :deep(.md-spoiler:not(.revealed) *) {
  color: transparent;
}
</style>
